import { useMemo } from "react";
import type { ResolvedJob, TimeRange } from "../types/job";

const MS_DAY = 86_400_000;

const RANGE_MS: Record<TimeRange, number> = {
  "24h": MS_DAY,
  "7d": 7 * MS_DAY,
};

/**
 * - Future `postedAt`: treated as “current” so listings aren’t hidden if the date is wrong.
 * - Invalid ISO: still show the job (bad imports shouldn’t empty the list).
 */
function withinTimeRange(postedAt: string, range: TimeRange): boolean {
  const postedMs = new Date(postedAt).getTime();
  if (!Number.isFinite(postedMs)) return true;
  const now = Date.now();
  const age = Math.max(0, now - postedMs);
  return age <= RANGE_MS[range];
}

/**
 * Filters by posted time and search only. Map viewport does not hide listings — choosing a job
 * from the list flies the map to its coordinates (`onSelectJobOnMap` in App).
 */
export function useFilteredJobs(
  jobs: ResolvedJob[],
  timeRange: TimeRange,
  searchQuery: string,
): ResolvedJob[] {
  return useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return jobs.filter((job) => {
      if (!withinTimeRange(job.postedAt, timeRange)) return false;
      if (!q) return true;
      return (
        job.title.toLowerCase().includes(q) ||
        job.company.toLowerCase().includes(q)
      );
    });
  }, [jobs, timeRange, searchQuery]);
}
